<template>
    <div class="contact-us-form-container px-2 h-max-100 overflow-y-auto py-3 w-100" :style="containerStyle">
        <template v-if="isStatusDone">
            <div class="w-100 mb-4">
                <div class="font-weight-bold font-size-200 text-center mb-5">
                    {{ 'How can we help?' | trans }}
                </div>

              <div v-if="education" class="text-center mx-auto mb-4">
                <span class="font-size-125 font-weight-bold">{{"We could not automatically verify by your email address that you are a teacher or student. Please contact us to get your free educational account."|trans}}</span>
              </div>

                <div class="mx-auto p-0 mb-4">
                    <select class="w-100 p-2 br-4 border" v-model="questionType">
                        <option value="question">{{ 'Ask a question' | trans }}</option>
                        <option value="issue">{{ 'Report an issue' | trans }}</option>
                        <option value="feature">{{ 'Suggest a feature' | trans }}</option>
                        <option value="sales">{{ 'Contact sales' | trans }}</option>
                        <option value="accounting">{{ 'Contact accounting' | trans }}</option>
                    </select>
                </div>

                <div class="mx-auto p-0 mb-4">
                    <TextArea
                        name="form"
                        type="text"
                        v-model="inputForm"
                        :label="getPlaceholder"
                        :max-characters="4000"
                    ></TextArea>
                </div>

                <div class="mx-auto p-0 mb-4">
                    <TextInput
                        name="email"
                        type="email"
                        autocomplete="on"
                        v-model="inputEmail"
                        :trim-input="true"
                        :label="'E-Mail' | trans"
                        icon="far fa-envelope"
                        :has-error="invalidEmail"
                        :current-error="getInvalidEmailError"
                    ></TextInput>
                </div>

                <div v-if="isUser !== true" class="mx-auto mb-4 card p-4 mb-5">
                    <label class="px-0 mb-0">
                        <input type="checkbox" id="acceptTos" class="m-2" v-model="acceptTos" /><span
                            v-html="getTosText"
                        ></span>
                    </label>
                </div>

                <div class="mx-auto p-0 mb-4 text-center">
                    <button
                        v-if="ticketCreated !== true"
                        class="btn btn-lg btn-success"
                        :class="{ 'col-5': buttonBlock === false, 'btn-block': buttonBlock === true }"
                        id="formSend"
                        @click.stop.prevent="submitForm"
                        @ticket-created="onTicketCreated"
                        :disabled="!canSubmit"
                    >
                        {{ 'Send' | trans }}
                    </button>
                </div>
            </div>

            <div class="w-100 text-center">
                <span
                    v-if="ticketCreated === 'failed'"
                    class="w-fit-content m-auto p-2 badge badge-danger text-center font-size-100"
                    >{{ 'Something went wrong. Please try again later.' | trans }}
                </span>
                <span
                    v-else-if="ticketCreated === true"
                    class="w-fit-content m-auto p-2 badge badge-success text-center font-size-100"
                    >{{ 'Thank you! We have recieved your request and will get back to you soon.' | trans }}
                </span>
                <div class="w-100" v-if="ticketCreated === true">
                    <button class="small w-fit-content btn btn-info mt-2 p-1" @click.stop.prevent="prepareNewRequest">
                        {{ 'Send another request' | trans }}
                    </button>
                </div>
                <div v-if="showBackButton">
                    <button class="btn btn-lg" @click.stop.prevent="onBackButtonClick">{{ 'Back' | trans }}</button>
                </div>
            </div>
        </template>

        <LoadingAnimation v-else-if="isStatusLoading" animation-style="bars"></LoadingAnimation>
    </div>
</template>

<script>
import LoadingAnimation from './LoadingAnimation';
import DataStatus from './mixins/DataStatus';
import TransFilter from './mixins/TransFilter';
import { translate } from '../../../helper/translate';
import { globalLogger } from '../../../helper/global-logger';
import { pageNotification } from '../../../helper/notify';
import { satGlobals } from '../../../sat/helper/sat-globals';
import TextInput from './form/TextInput';
import TextArea from './form/TextArea';
import { testIsEmailValid } from '../../../../lib-sat/user/test';
import { CONTACT_US_CLOSE_MODAL } from '../../../../lib-sat/contact-us/event';
import { apiRequestManager } from '../../../../lib-sat/api/api-request-manager';
import { csrfManager } from '../../../../lib-sat/csrf/csrf-manager';
import { userManager } from '../../../../lib-sat/user/user-manager';
import { testIsEmptyString, testIsFunction } from '../../../../lib/test-and-assert/test-base';
import { eventBus } from '../../../../lib/event-bus/event-bus';

export default {
    name: 'ContactUsForm',
    components: { LoadingAnimation, TextInput, TextArea },
    mixins: [DataStatus, TransFilter],
    mounted() {
        this.setStatusLoading();

        this.questionType = this.defaultQuestionType;

        userManager.getUser().then((user) => {
            if (user.isRegistered()) {
                this.isUser = true;
                this.acceptTos = true;
                this.inputEmail = user.getPlainEmail();
            }

            this.setStatusDone();
        });
    },
    methods: {
        submitForm() {
            if (!testIsEmailValid(this.inputEmail)) {
                this.invalidEmail = true;
                return;
            }

            const data = {
                email: this.inputEmail,
                message: this.inputForm,
                type: this.questionType,
            };

            this.setStatusLoading();

            csrfManager
                .getCsrfContactUs()
                .then((token) => {
                    data._csrf = token;

                    return apiRequestManager.postWithCaptcha('api/contact-us', data);
                })
                .then(() => {
                    pageNotification.success('Thank you! We have recieved your request and will get back to you soon.');
                    this.ticketCreated = true;
                    eventBus.emit(CONTACT_US_CLOSE_MODAL);
                    globalLogger.log('contact us form - sending request success');
                })
                .catch(() => {
                    this.ticketCreated = 'failed';
                    globalLogger.log('contact us form - sending request fail');

                    pageNotification.showGenericErrorMessage();
                })
                .finally(() => {
                    this.setStatusDone();
                });
        },
        onTicketCreated() {
            this.ticketCreated = true;
        },
        prepareNewRequest() {
            this.setStatusLoading();
            this.clearInputs();
            this.setStatusDone();
        },
        clearInputs() {
            this.inputForm = '';

            if (this.isUser !== true) {
                this.acceptTos = false;
            }

            this.ticketCreated = false;
            this.questionType = 'question';
        },
        onBackButtonClick() {
            if (testIsFunction(this.backButtonAction)) {
                this.backButtonAction();

                return;
            }

            try {
                this.$router.back();
            } catch (e) {
                globalLogger.log('contact us form - back button fail', e);

                pageNotification.showGenericErrorMessage();
            }
        },
    },
    props: {
        buttonBlock: {
            type: Boolean,
            default: false,
        },
        defaultQuestionType: {
            type: String,
            default: 'question',
        },
        showBackButton: {
            type: Boolean,
            default: false,
        },
        backButtonAction: {
            type: Function,
            default: null,
        },
        containerStyle: {
            type: String,
            default: '',
        },
        education: {
            required: false,
            default: false,
            type: Boolean,
        },
    },
    data() {
        return {
            inputForm: '',
            inputEmail: '',
            acceptTos: false,
            questionType: 'question',
            qgContactUsCaptchaInit: false,
            captchaToken: 'no-token',
            ticketCreated: false,
            invalidEmail: false,
            isUser: false,
            csrfToken: '',
        };
    },
    computed: {
        termsOfUseLink: function () {
            return satGlobals.getTermsUrl();
        },
        privacyPolicyLink: function () {
            return satGlobals.getPrivacyUrl();
        },
        getPlaceholder: function () {
            return translate('What can we do for you?');
        },
        getTosText: function () {
            let translated = translate(
                `I accept the <a href=\"%termsOfUseLink%\">Terms of use</a> and the <a href=\"%privacyPolicyLink%\">Privacy Policy</a>`
            );

            return translated
                .replace('%termsOfUseLink%', this.termsOfUseLink)
                .replace('%privacyPolicyLink%', this.privacyPolicyLink);
        },
        getInvalidEmailError: function () {
            return translate('Please enter a valid email');
        },
        canSubmit() {
            return (
                this.acceptTos === true &&
                !testIsEmptyString(this.inputEmail) &&
                !testIsEmptyString(this.inputForm) &&
                this.ticketCreated === false
            );
        },
    },
};
</script>
